import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Box,
  Alert,
  Snackbar,
  MenuItem,
  InputLabel,
  Select
} from '@mui/material';
import axios from 'axios';
import { format, isSunday, isWednesday, isAfter } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { getCurrentOrganization } from '../utils/organizationContext';
import FormOrganizationBanner from './FormOrganizationBanner';
import countries from '../data/countries';

const NewConvertForm = () => {
  const navigate = useNavigate();
  
  // Get today's date in YYYY-MM-DD format, adjusted for timezone
  const today = new Date();
  const localDate = format(today, 'yyyy-MM-dd');
  
  const [formData, setFormData] = useState({
    date: localDate,
    first_name: '',
    last_name: '',
    street_address: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'Canada', // Default to Canada
    phone_number: '',
    email: '',
    prayer_request: '',
    requires_counselling: 'no',
    counselling_area: '',
    conversion_type: 'first_time'
  });
  
  const [dateError, setDateError] = useState('');

  // Validate service date (must be Sunday or Wednesday, not in the future)
  const validateServiceDate = (dateString) => {
    if (!dateString) {
      setDateError('Date is required');
      return false;
    }
    
    const selectedDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    
    if (isAfter(selectedDate, today)) {
      setDateError('Date cannot be in the future');
      return false;
    }
    
    if (!isSunday(selectedDate) && !isWednesday(selectedDate)) {
      setDateError('Date must be a Sunday or Wednesday');
      return false;
    }
    
    setDateError('');
    return true;
  };

  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const { organizationSlug } = useAuth();
  const [currentOrgSlug, setCurrentOrgSlug] = useState(null);

  // Get organization from URL
  useEffect(() => {
    const slug = organizationSlug || getCurrentOrganization();
    setCurrentOrgSlug(slug);
    
    // If no organization found and not in an organization route, redirect to welcome page
    if (!slug && !organizationSlug && window.location.pathname === '/new-convert') {
      window.location.href = '/';
    }
  }, [organizationSlug]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Validate date when it changes
    if (name === 'date') {
      validateServiceDate(value);
    }
  };

  // Function to check for duplicates
  const checkForDuplicate = async (data) => {
    try {
      setIsCheckingDuplicate(true);
      setDuplicateInfo(null);
      
      const checkData = {
        email: data.email || '',
        phone_number: data.phone_number || '',
        date: data.date,
        ...(currentOrgSlug && { organization_slug: currentOrgSlug })
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/new-converts/check-duplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(checkData),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.isDuplicate) {
          setDuplicateInfo(result.duplicate);
          return true; // Duplicate found
        }
      }
      return false; // No duplicate
    } catch (error) {
      console.error('Error checking for duplicate:', error);
      return false; // Continue with submission if check fails
    } finally {
      setIsCheckingDuplicate(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setDuplicateInfo(null);
      
      // Validate date before submission
      if (!validateServiceDate(formData.date)) {
        return; // Stop submission if date is invalid
      }
      
      // First check for duplicates
      const isDuplicate = await checkForDuplicate(formData);
      if (isDuplicate) {
        return; // Stop submission if duplicate found
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/new-converts`,
        {
          ...formData,
          requires_counselling: formData.requires_counselling === 'yes',
          ...(currentOrgSlug && { organization_slug: currentOrgSlug })
        }
      );

      if (response.status === 201) {
        setShowSuccess(true);
        setTimeout(() => {
          // Navigate to organization-prefixed thank-you page
          const thankYouPath = currentOrgSlug ? `/${currentOrgSlug}/thank-you` : '/thank-you';
          navigate(thankYouPath);
        }, 2000);
      }
    } catch (err) {
      // Handle duplicate error from backend
      if (err.response?.status === 409) {
        setDuplicateInfo(err.response.data.duplicate);
        setError(err.response.data.message || 'You have already registered for this date.');
        return;
      }
      
      setError(err.response?.data?.error || 'Failed to submit form');
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center" sx={{ color: 'primary.main', mb: 1 }}>
          New Converts - Thank you for accepting Jesus
        </Typography>
        <FormOrganizationBanner subtitle="New convert registration for this church branch" />
        <Typography 
          variant="body2" 
          align="center" 
          sx={{ 
            mb: 4, 
            fontStyle: 'italic',
            color: 'text.secondary',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          "Therefore if any man be in Christ, he is a new creature: old things are passed away; behold, all things are become new." - 2 Corinthians 5:17 (KJV)
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                name="date"
                label="Date"
                value={formData.date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
                error={!!dateError}
                helperText={dateError || 'Must be a Sunday or Wednesday'}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset">
                <Typography variant="subtitle1" gutterBottom>
                  Conversion Type
                </Typography>
                <RadioGroup
                  name="conversion_type"
                  value={formData.conversion_type}
                  onChange={handleChange}
                  row
                >
                  <FormControlLabel value="first_time" control={<Radio />} label="First Time Salvation" />
                  <FormControlLabel value="rededication" control={<Radio />} label="Rededication to Christ" />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="first_name"
                label="First Name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="last_name"
                label="Last Name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                name="street_address"
                label="Street Address"
                value={formData.street_address}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="city"
                label="City"
                value={formData.city}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="state"
                label="State/Province"
                value={formData.state}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="postal_code"
                label="Postal Code"
                value={formData.postal_code}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Country</InputLabel>
                <Select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  label="Country"
                >
                  {countries.map((country) => (
                    <MenuItem key={country.code} value={country.name}>
                      {country.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="phone_number"
                label="Phone Number"
                value={formData.phone_number}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                name="prayer_request"
                label="Prayer Request"
                multiline
                rows={4}
                value={formData.prayer_request}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset">
                <Typography variant="subtitle1" gutterBottom>
                  Do you require further counselling?
                </Typography>
                <RadioGroup
                  name="requires_counselling"
                  value={formData.requires_counselling}
                  onChange={handleChange}
                  row
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>

            {formData.requires_counselling === 'yes' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="counselling_area"
                  label="In what area do you need counselling?"
                  multiline
                  rows={2}
                  value={formData.counselling_area}
                  onChange={handleChange}
                  required
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={isCheckingDuplicate}
                >
                  {isCheckingDuplicate ? 'Checking...' : 'Submit'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>

        <Snackbar
          open={showSuccess}
          autoHideDuration={2000}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="success">
            Form submitted successfully! Redirecting...
          </Alert>
        </Snackbar>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {duplicateInfo && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Duplicate Registration Detected
            </Typography>
            <Typography variant="body2">
              A registration already exists for <strong>{duplicateInfo.name}</strong> on <strong>{duplicateInfo.date}</strong>.
            </Typography>
            {duplicateInfo.email && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Email: {duplicateInfo.email}
              </Typography>
            )}
            {duplicateInfo.phone && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Phone: {duplicateInfo.phone}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              If this is a different person, please contact the church office for assistance.
            </Typography>
          </Alert>
        )}

        {isCheckingDuplicate && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Checking for existing registrations...
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default NewConvertForm; 